import { useState } from 'react';

import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RestoreIcon from '@mui/icons-material/Restore';
import { Box, Button, MenuItem, Select } from '@mui/material';

import { useMutation } from '@redwoodjs/web';

import { ConfirmationModal } from 'src/components/Modals';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { UserStatuses } from 'src/constants';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { RESEND_EMAIL_INVITE } from 'src/graphql/mutations/login.mutation';
import { UPDATE_USER_MUTATION } from 'src/graphql/mutations/updateUser.mutation';

interface UserStatusProps {
    status: string;
    userName?: string;
    userId: number;
}

const UserStatus = ({ status, userName, userId }: UserStatusProps) => {
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const { logout } = useAuthContext();
    const [currentStatus, setCurrentStatus] = useState(status);

    const [updateUser] = useMutation(UPDATE_USER_MUTATION, {
        onCompleted: ({ updateUser: { status: updatedUserStatus } }) => {
            const confirmationMessage =
                currentStatus === UserStatuses.SUSPENDED &&
                updatedUserStatus === UserStatuses.ACTIVE
                    ? 'User unsuspended successfully'
                    : 'Status updated Successfully';
            showSuccessToast(confirmationMessage);
            setCurrentStatus(updatedUserStatus);
        },
        onError: () => {
            showErrorToast('Error Changing User Status');
        },
    });

    const [resendEmailInvitation] = useMutation(RESEND_EMAIL_INVITE, {
        onCompleted: () => {
            showSuccessToast('Email Sent Successfully');
            updateUser({
                variables: {
                    id: userId,
                    input: {
                        status: UserStatuses.PENDING,
                    },
                },
            });
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Resending Activation Email');
        },
    });

    const handleUserStatusChange = (newStatus) => {
        updateUser({
            variables: {
                id: userId,
                input: {
                    status: newStatus,
                },
            },
        });
        if (newStatus === UserStatuses.ACTIVE) {
            setIsConfirmationModalOpen(false);
        }
    };

    const resendActivationEmail = () => {
        resendEmailInvitation({ variables: { id: userId } });
    };

    const handleStatusDropDownChange = async (event: any) => {
        setCurrentStatus(event.target.value);
        handleUserStatusChange(event.target.value);
        if (event.target.value === UserStatuses.NOT_ACTIVATED) {
            logout();
        }
    };

    const filteredStatuses =
        currentStatus === UserStatuses.ACTIVE
            ? Object.values(UserStatuses)
                  .filter((status) => status !== UserStatuses.PENDING)
                  .filter((status) => status !== UserStatuses.NOT_ACTIVATED)
            : Object.values(UserStatuses)
                  .map((status) => status.replace('_', ' '))
                  .filter((status) => status !== UserStatuses.PENDING);

    return (
        <Box sx={{ display: 'flex', gap: '24px', justifyContent: 'flex-end' }}>
            {/* conditionals */}
            {currentStatus === UserStatuses.NOT_ACTIVATED && (
                <Box display="flex" alignItems="center" gap="16px">
                    <StatusChip type="user" status={currentStatus} />

                    <Button
                        startIcon={<RestoreIcon />}
                        variant="npi-primary"
                        onClick={resendActivationEmail}
                    >
                        Resend Activation
                    </Button>
                </Box>
            )}
            {currentStatus === UserStatuses.ACTIVE || currentStatus === UserStatuses.SUSPENDED ? (
                <Box display="flex" alignItems="center" gap="16px">
                    <Box>
                        <Select
                            fullWidth
                            disabled={currentStatus === UserStatuses.SUSPENDED}
                            sx={{
                                width: '136px',
                                color: '#F18F34',
                                bgcolor: 'white',
                                height: '40px',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                            value={currentStatus}
                            onChange={handleStatusDropDownChange}
                            name="userStatus"
                        >
                            {filteredStatuses.map((status) => (
                                <MenuItem value={status} key={status} sx={{ alignSelf: 'center' }}>
                                    <StatusChip type="user" status={status} />
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {currentStatus === UserStatuses.ACTIVE && (
                        <Button
                            startIcon={<BlockIcon />}
                            variant="npi-primary"
                            onClick={() => handleUserStatusChange(UserStatuses.SUSPENDED)}
                        >
                            Suspend
                        </Button>
                    )}

                    {currentStatus === UserStatuses.SUSPENDED && (
                        <Button
                            startIcon={<RestoreIcon />}
                            variant="npi-primary"
                            onClick={() => {
                                if (!isConfirmationModalOpen) {
                                    setIsConfirmationModalOpen(!isConfirmationModalOpen);
                                }
                            }}
                        >
                            Unsuspend
                        </Button>
                    )}

                    <Button
                        startIcon={<RestoreIcon />}
                        variant="npi-primary"
                        onClick={resendActivationEmail}
                    >
                        Reset Password
                    </Button>
                </Box>
            ) : null}
            {currentStatus === UserStatuses.LOCKED && (
                <Box display="flex" alignItems="center" gap="16px">
                    <StatusChip type="user" status={currentStatus} />

                    <Button
                        startIcon={<LockOpenIcon />}
                        variant="npi-primary"
                        onClick={() => handleUserStatusChange(UserStatuses.ACTIVE)}
                    >
                        Unlock
                    </Button>
                </Box>
            )}

            {currentStatus === UserStatuses.PENDING && (
                <Box display="flex" alignItems="center" gap="16px">
                    <StatusChip type="user" status={currentStatus} />
                </Box>
            )}
            <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                title="Unsuspend user"
                message={`Are you sure you want to unsuspend ${userName}?`}
                onCancel={() => {
                    setIsConfirmationModalOpen(false);
                }}
                onConfirm={() => {
                    handleUserStatusChange(UserStatuses.ACTIVE);
                }}
            />
        </Box>
    );
};

export default UserStatus;
